import { call, put, takeEvery, select, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  packingListApi,
  SearchPackingListsRequest,
  SearchPackingListsResponse,
  CreatePackingListRequest,
  PackingList,
  PackingListDetail,
  ShipToLocation,
  Product,
  PackingListReportData,
} from '../../services/packingListApi';
import {
  fetchPackingListsStart,
  fetchPackingListsSuccess,
  fetchPackingListsFailure,
  fetchPackingListByIdStart,
  fetchPackingListByIdSuccess,
  fetchPackingListByIdFailure,
  createPackingListStart,
  createPackingListSuccess,
  createPackingListFailure,
  updatePackingListStart,
  updatePackingListSuccess,
  updatePackingListFailure,
  deletePackingListStart,
  deletePackingListSuccess,
  deletePackingListFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  fetchDetailsStart,
  fetchDetailsSuccess,
  fetchDetailsFailure,
  addDetailStart,
  addDetailSuccess,
  addDetailFailure,
  updateDetailStart,
  updateDetailSuccess,
  updateDetailFailure,
  deleteDetailStart,
  deleteDetailSuccess,
  deleteDetailFailure,
  generateReportStart,
  generateReportSuccess,
  generateReportFailure,
} from '../reducers/packingListSlice';
import { RootState } from '../index';

// Fetch Packing Lists
function* fetchPackingListsApi(action: PayloadAction<SearchPackingListsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { 
      currentPage, 
      pageSize, 
      searchTerm, 
      activeFilter 
    } = state.packingList;

    const params: SearchPackingListsRequest = {
      pageNum: currentPage,
      pageSize: pageSize,
      sort: 'PackingListNum asc',
      filter: {
        ...(action.payload?.filter || {}),
        ...(activeFilter !== null && { Active: activeFilter }),
        ...(searchTerm && { PackingListName: searchTerm }),
      },
      ...action.payload,
    };

    const response: SearchPackingListsResponse = yield call(packingListApi.getAll, params);
    yield put(fetchPackingListsSuccess(response));
  } catch (error: any) {
    yield put(fetchPackingListsFailure(error.message || 'Failed to fetch packing lists'));
  }
}

// Fetch Packing List by ID
function* fetchPackingListByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const packingList: PackingList = yield call(packingListApi.getById, action.payload);
    yield put(fetchPackingListByIdSuccess(packingList));
    
    // Also fetch details for this packing list
    yield put(fetchDetailsStart(action.payload));
  } catch (error: any) {
    yield put(fetchPackingListByIdFailure(error.message || 'Failed to fetch packing list'));
  }
}

// Create Packing List
function* createPackingListApi(action: PayloadAction<CreatePackingListRequest>): Generator<any, void, any> {
  try {
    // Validate the packing list data
    const validationErrors: string[] = yield call(packingListApi.validatePackingList, action.payload);
    
    if (validationErrors.length > 0) {
      yield put(createPackingListFailure(`Validation failed: ${validationErrors.join(', ')}`));
      return;
    }

    const newPackingList: PackingList = yield call(packingListApi.create, action.payload);
    yield put(createPackingListSuccess(newPackingList));

    // Refresh the packing lists to show the new item
    yield put(fetchPackingListsStart());
  } catch (error: any) {
    yield put(createPackingListFailure(error.message || 'Failed to create packing list'));
  }
}

// Update Packing List
function* updatePackingListApi(action: PayloadAction<Partial<PackingList>>): Generator<any, void, any> {
  try {
    // Validate the packing list data
    const validationErrors: string[] = yield call(packingListApi.validatePackingList, action.payload);
    
    if (validationErrors.length > 0) {
      yield put(updatePackingListFailure(`Validation failed: ${validationErrors.join(', ')}`));
      return;
    }

    const updatedPackingList: PackingList = yield call(packingListApi.update, action.payload);
    yield put(updatePackingListSuccess(updatedPackingList));
  } catch (error: any) {
    yield put(updatePackingListFailure(error.message || 'Failed to update packing list'));
  }
}

// Delete Packing List
function* deletePackingListApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(packingListApi.delete, action.payload);
    yield put(deletePackingListSuccess(action.payload));
  } catch (error: any) {
    yield put(deletePackingListFailure(error.message || 'Failed to delete packing list'));
  }
}

// Fetch Dropdown Data
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    // Fetch all dropdown data in parallel
    const [shipToLocations, products]: [ShipToLocation[], Product[]] = yield all([
      call(packingListApi.getShipToLocations),
      call(packingListApi.getProducts)
    ]);

    yield put(fetchDropdownsSuccess({
      shipToLocations,
      products
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Fetch Packing List Details
function* fetchDetailsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const details: PackingListDetail[] = yield call(packingListApi.getDetails, action.payload);
    yield put(fetchDetailsSuccess(details));
  } catch (error: any) {
    yield put(fetchDetailsFailure(error.message || 'Failed to fetch packing list details'));
  }
}

// Add Detail
function* addDetailApi(action: PayloadAction<{ packingListId: string; detail: Omit<PackingListDetail, 'id' | 'PackingListId'> }>): Generator<any, void, any> {
  try {
    const newDetail: PackingListDetail = yield call(
      packingListApi.addDetail, 
      action.payload.packingListId, 
      action.payload.detail
    );
    yield put(addDetailSuccess(newDetail));
  } catch (error: any) {
    yield put(addDetailFailure(error.message || 'Failed to add detail'));
  }
}

// Update Detail
function* updateDetailApi(action: PayloadAction<PackingListDetail>): Generator<any, void, any> {
  try {
    const updatedDetail: PackingListDetail = yield call(packingListApi.updateDetail, action.payload);
    yield put(updateDetailSuccess(updatedDetail));
  } catch (error: any) {
    yield put(updateDetailFailure(error.message || 'Failed to update detail'));
  }
}

// Delete Detail
function* deleteDetailApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(packingListApi.deleteDetail, action.payload);
    yield put(deleteDetailSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteDetailFailure(error.message || 'Failed to delete detail'));
  }
}

// Generate Report
function* generateReportApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const reportData: PackingListReportData = yield call(packingListApi.generateReport, action.payload);
    yield put(generateReportSuccess(reportData));
  } catch (error: any) {
    yield put(generateReportFailure(error.message || 'Failed to generate report'));
  }
}

// Watch for filter changes
function* watchFilters(): Generator<any, void, any> {
  yield takeEvery(['packingList/setSearchTerm', 'packingList/setActiveFilter'], function* () {
    yield put(fetchPackingListsStart());
  });
}

// Watch for pagination changes
function* watchPagination(): Generator<any, void, any> {
  yield takeEvery(['packingList/setPage', 'packingList/setPageSize'], function* () {
    yield put(fetchPackingListsStart());
  });
}

// Root Packing List Saga
export default function* packingListSaga(): Generator<any, void, any> {
  // Main API calls
  yield takeEvery(fetchPackingListsStart.type, fetchPackingListsApi);
  yield takeEvery(fetchPackingListByIdStart.type, fetchPackingListByIdApi);
  yield takeEvery(createPackingListStart.type, createPackingListApi);
  yield takeEvery(updatePackingListStart.type, updatePackingListApi);
  yield takeEvery(deletePackingListStart.type, deletePackingListApi);

  // Dropdown data
  yield takeEvery(fetchDropdownsStart.type, fetchDropdownsApi);

  // Details management
  yield takeEvery(fetchDetailsStart.type, fetchDetailsApi);
  yield takeEvery(addDetailStart.type, addDetailApi);
  yield takeEvery(updateDetailStart.type, updateDetailApi);
  yield takeEvery(deleteDetailStart.type, deleteDetailApi);

  // Report generation
  yield takeEvery(generateReportStart.type, generateReportApi);

  // Background tasks
  yield takeEvery('*', watchFilters);
  yield takeEvery('*', watchPagination);
} 